import { timelineItemClasses } from "@mui/lab";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import {
  Box,
  Button,
  Card,
  CircularProgress
} from "@mui/material";
import theme from "../../theme/MuiTheme";
import ClaimAction from "./actions/ClaimAction";
import React, { useCallback, useEffect, useState } from "react";
import NewMailModal from "./modals/NewMailModal";
import QaBanner from "./QaBanner";
import { useThemeContext } from "../../theme/ThemeContextProvider";
import { neutral, primary } from "../../theme/colors";
import { useTranslation } from "react-i18next";
import { typography } from "../../theme/typography";
import { ReactComponent as PlusIcon } from "../../assets/claim/plus.svg";
import useAuthenticatedFetch from "../../auth/authenticated";
import { Action } from "../../models/Action";
import { Claim } from "../../models/Claim";
import { FEEDBACK } from "../../models/Feedback";
import { User } from "../../models/User";
import Feedback from "../ui/Feedback";

interface ClaimActionsProps {
  user: User;
  claimId: string;
  claim: Claim;
  updateClaim: Function;
}

// Mirrors QA_RESOLUTION_CATEGORIES in the backend qa_manager: a QA finding is
// "resolved" by a separate action of one of these categories that references the
// QA_FINDING_CREATED row via qa_payload.resolves_action_id. We merge that resolution
// into the original finding card rather than rendering it as its own (textless) card.
const QA_RESOLUTION_CATEGORIES = ["QA_FINDING_RESOLVED", "QA_FINDING_DISMISSED"];

const ClaimActions: React.FC<ClaimActionsProps> = ({ user, claimId, claim, updateClaim }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const getAccessHeader = useAuthenticatedFetch();

  // state hooks
  const [openNewMailModal, setNewMailModalOpen] = useState(false);
  const [schemaError, setSchemaError] = useState(false);
  const [attachmentSchema, setAttachmentSchema] = useState();
  const [actionsError, setActionsError] = useState(false);
  const [actions, setActions] = useState<Action[]>([]);
  const [showFeedback, setShowFeedback] = useState<FEEDBACK>(FEEDBACK.NONE);
  const [feedbackSuccessText, setFeedbackSuccessText] = useState<string>(t("GENERAL.SAVED"));

  const styling = {
    color: mode === "light" ? neutral[200] : neutral[600],
  };

  // Map each QA finding id -> its resolution action, and drop resolution actions
  // from the rendered timeline so they don't appear as standalone empty cards.
  const resolutionByFindingId = new Map<string, Action>();
  actions.forEach((a) => {
    if (QA_RESOLUTION_CATEGORIES.includes(a.category)) {
      const findingId = a.qa_payload?.resolves_action_id;
      if (findingId) {
        resolutionByFindingId.set(findingId, a);
      }
    }
  });
  const visibleActions = actions.filter(
    (a) => !QA_RESOLUTION_CATEGORIES.includes(a.category),
  );

  const fetchActions = useCallback(async () => {
    const response = await fetch(`/api/claims/${claimId}/actions`, {
      method: "GET",
      headers: await getAccessHeader(false),
    });

    if (!response.ok) {
      console.error("Network response was not ok");
      setActionsError(true);
      setShowFeedback(FEEDBACK.ERROR);
    } else {
      const data = await response.json();
      setActions(data.actions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const refreshClaim = useCallback(async () => {
    const response = await fetch(`/api/claims/${claimId}`, {
      method: "GET",
      headers: await getAccessHeader(false),
    });
    if (response.ok) {
      const data = await response.json();
      updateClaim(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const handleQaResolved = useCallback(
    async (released: boolean) => {
      if (released) {
        // Match ResolveModal: claim left the operator worklist.
        window.location.reload();
        return;
      }
      await Promise.all([fetchActions(), refreshClaim()]);
    },
    [fetchActions, refreshClaim],
  );

  useEffect(() => {
    const fetchAttachmentsSchema = async () => {
      const response = await fetch(`/api/attachments/schema`, {
        method: "GET",
        headers: await getAccessHeader(false),
      });

      if (!response.ok) {
        console.error("Network response was not ok");
        setSchemaError(true);
        setShowFeedback(FEEDBACK.ERROR);
      } else {
        const data = await response.json();
        setAttachmentSchema(data);
      }
    };

    fetchActions().then();
    fetchAttachmentsSchema().then();
    // eslint-disable-next-line
  }, [fetchActions, claim.claim_status_id, claim.wakeup_time]);

  const handleDeleteEmail = async (actionId: string) => {
    const response = await fetch(`/api/actions/${actionId}`, {
      method: "DELETE",
      headers: await getAccessHeader(),
    });

    if (!response.ok) {
      console.error("Network response was not ok");
      setActionsError(true);
      setShowFeedback(FEEDBACK.ERROR);
    } else {
      // remove action from view
      setActions((prevActions) =>
        prevActions.filter((action) => action.id !== actionId),
      );
      triggerFeedback(true, 'DASHBOARD.ACTIONS.DELETE_MAIL_SUCCESS');
    }
  };

  /**
   * Feedback callback for child components
   * @param success - true for success / false for error
   * @param successTextToken - optional success text token. Default is 'GENERAL.SAVED'.
   */
  const triggerFeedback = (success: boolean, successTextToken: string = 'GENERAL.SAVED') => {
    setFeedbackSuccessText(t(successTextToken));
    setShowFeedback(success ? FEEDBACK.SUCCESS : FEEDBACK.ERROR);
  };

  return (
    <Box
      flex={8}
      sx={{
        pt: "24px",
        height: "100vh",
        overflowY: "scroll",
        scrollbarWidth: "none" /* Firefox */,
        "&::-webkit-scrollbar": { display: "none" },
        [theme.breakpoints.down("lg")]: {
          overflow: "visible",
          height: "100%",
        },
      }}
    >
      <QaBanner
        claimId={claimId}
        openQaFindingIds={claim.open_qa_finding_ids || []}
        actions={actions}
        onResolved={handleQaResolved}
      />

      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
          paddingRight: 0,
          paddingTop: 0,
          paddingLeft: 0,
          margin: 0,
        }}
      >
        {/* New Mail */}
        <TimelineItem sx={{ marginBottom: "24px" }}>
          <TimelineSeparator>
            <TimelineDot
              variant="outlined"
              sx={{
                borderColor: styling.color,
                marginTop: 0,
                marginBottom: "8px",
              }}
            />
            <TimelineConnector
              sx={{
                borderLeftStyle: "dashed",
                borderLeftWidth: "2px",
                backgroundColor: "transparent",
                color: styling.color,
              }}
            />
          </TimelineSeparator>
          <TimelineContent
            sx={{
              p: 0,
              paddingLeft: 2,
            }}
          >
            <Card style={{ height: "56px" }}>
              <Button
                style={{ width: "100%", height: "inherit" }}
                onClick={() => setNewMailModalOpen(true)}
              >
                <PlusIcon
                  style={{ verticalAlign: "middle", marginRight: "8px" }}
                />
                <p style={{ ...typography.buttonLg, color: primary[600] }}>
                  {t("DASHBOARD.ACTIONS.NEW_MAIL")}
                </p>
              </Button>
            </Card>
          </TimelineContent>
        </TimelineItem>

        {/* N Mails */}
        {actionsError ? (
          <Box sx={{ pt: 3, m: 5, textAlign: "center" }}>
            {t("GENERAL.ERROR")}
          </Box>
        ) : (
          <>
            {actions.length === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress />
              </Box>
            )}

            {visibleActions.length > 0 &&
              visibleActions.map((a, index) => {
                return (
                  <React.Fragment key={`item-${index}`}>
                    <TimelineItem
                      key={a.timestamp}
                      sx={{ marginBottom: "24px" }}
                    >
                      <TimelineSeparator>
                        <TimelineDot
                          variant="outlined"
                          sx={{
                            borderColor: styling.color,
                            marginTop: 0,
                          }}
                        />
                        <TimelineConnector
                          sx={{ backgroundColor: styling.color }}
                        />
                      </TimelineSeparator>
                      <TimelineContent
                        sx={{
                          p: 0,
                          paddingLeft: 2,
                        }}
                      >
                        <ClaimAction
                          action={a}
                          key={index}
                          attachmentSchema={attachmentSchema}
                          error={schemaError}
                          isOpen={index === 0}
                          user={user}
                          handleDeleteEmail={handleDeleteEmail}
                          triggerFeedback={triggerFeedback}
                          resolution={resolutionByFindingId.get(a.id)}
                        />
                      </TimelineContent>
                    </TimelineItem>
                  </React.Fragment>
                );
              })}
          </>
        )}
      </Timeline>

      {/* Modals */}
      <NewMailModal
        isOpen={openNewMailModal}
        onClose={() => setNewMailModalOpen(false)}
        claimId={claimId}
      />

      {/* Request Feedback */}
      <Feedback type={showFeedback} onClose={setShowFeedback} successMessage={feedbackSuccessText}></Feedback>
    </Box>
  );
};

export default ClaimActions;
